import type {
  SealStatusValue,
  TankStatusValue,
  TaskTypeValue,
} from '@my/tms-api';
import { SelectInput, TextInput } from '@my/ui';
import { cargoHandlingPhaseFor } from '../lib/cargoPick';
import type {
  CargoFamily,
  CargoItemDraft,
  CargoProductOption,
  PalletVariantOption,
} from '../lib/types';
import { CargoProductLines } from './CargoProductLines';
import { NumberFieldInput } from './inputs';

/**
 * Per-task cargo inputs, family-aware. At perTrailer = 2, container /
 * tank tasks render two parallel rows (one per physical unit).
 * Pallet tasks always render one line.
 */
export interface CargoItemInputsSectionProps {
  family: CargoFamily;
  /**
   * Drives the load-vs-delivery shape. Currently used only by container
   * (Load/PickUp = load-side BIC+Seal+Weight+Volume; Unload/DropOff/
   * Deliver = delivery-side BIC+Seal Status+Offload+Volume). Tank +
   * pallet ignore until their delivery-side gets spec'd.
   */
  taskType: TaskTypeValue;
  drafts: CargoItemDraft[];
  disabled: boolean;
  /**
   * Pallet types for the Pallet Type picker (pallet family only). The
   * picked variant's `name` is stored in `draft.description`; its
   * `loadingMetres` feeds the read-only loading-metres calculation.
   * Empty for non-pallet families.
   */
  palletVariants?: PalletVariantOption[];
  /**
   * Products a collection row may carry; null / absent hides the picker
   * (products off, or the cargo type has no Products property).
   */
  productOptions?: CargoProductOption[] | null;
  /** The cargo type has Compartments — several products per unit. */
  multiProduct?: boolean;
  onChange: (index: number, patch: Partial<CargoItemDraft>) => void;
}

export function CargoItemInputsSection({
  family,
  taskType,
  drafts,
  disabled,
  palletVariants = [],
  productOptions = null,
  multiProduct = false,
  onChange,
}: CargoItemInputsSectionProps) {
  // Per-unit labels only when there's more than one row.
  const showUnitLabels = drafts.length > 1;
  return (
    <div className="space-y-3">
      {drafts.map((draft, idx) => (
        <CargoItemRow
          key={idx}
          index={idx}
          draft={draft}
          family={family}
          taskType={taskType}
          disabled={disabled}
          showUnitLabel={showUnitLabels}
          palletVariants={palletVariants}
          productOptions={productOptions}
          multiProduct={multiProduct}
          onChange={(patch) => onChange(idx, patch)}
        />
      ))}
    </div>
  );
}

/** One row of cargo-item inputs — fields shaped by `family` + `taskType`. */
export function CargoItemRow({
  index,
  draft,
  family,
  taskType,
  disabled,
  showUnitLabel,
  palletVariants = [],
  productOptions = null,
  multiProduct = false,
  onChange,
}: {
  index: number;
  draft: CargoItemDraft;
  family: CargoFamily;
  taskType: TaskTypeValue;
  disabled: boolean;
  showUnitLabel: boolean;
  palletVariants?: PalletVariantOption[];
  productOptions?: CargoProductOption[] | null;
  multiProduct?: boolean;
  onChange: (patch: Partial<CargoItemDraft>) => void;
}) {
  // Only the container family branches load vs delivery — tank + pallet
  // keep their single shape until their delivery-side fields are spec'd.
  const phase = cargoHandlingPhaseFor(taskType);

  // The Pallet Type picker only applies to the actual `Pallet` cargo type,
  // which is the only family member that ships type variants. Other members
  // of the 'pallet' family (FullLoad, AgriGrains, Construction, …) carry no
  // variants and keep the original free-text Description field.
  const hasPalletTypes = family === 'pallet' && palletVariants.length > 0;

  // Pallet type is stored by name in `description` (FE convention — there's
  // no dedicated column). Resolve the picked variant to surface its loading
  // metres, then multiply by the line quantity for the read-only total.
  const pickedPalletVariant = hasPalletTypes
    ? (palletVariants.find((v) => v.name === draft.description) ?? null)
    : null;
  const loadingMetresPerPallet = pickedPalletVariant?.loadingMetres ?? null;
  const totalLoadingMetres =
    loadingMetresPerPallet != null && draft.quantity != null
      ? loadingMetresPerPallet * draft.quantity
      : null;
  const loadingMetresDisplay =
    totalLoadingMetres != null ? `${totalLoadingMetres.toFixed(2)} m` : '—';

  // Pallet Type options. A blank placeholder, then one per variant. If the
  // draft already carries a description that isn't a known variant (legacy
  // free-text rows), surface it as an extra option so the value stays
  // visible and isn't silently dropped on the next save.
  const palletTypeOptions = hasPalletTypes
    ? [
        { value: '', label: '—' },
        ...palletVariants.map((v) => ({ value: v.name, label: v.name })),
        ...(draft.description && !palletVariants.some((v) => v.name === draft.description)
          ? [{ value: draft.description, label: draft.description }]
          : []),
      ]
    : [];

  return (
    // gap-3 between stacked lines, as the task form spaces its own fields —
    // the inputs' floating labels sit above the box and need the room.
    <div className="space-y-3">
      {showUnitLabel && (
        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
          Unit {index + 1}
        </p>
      )}
      {/* What this unit carries — entered where it is loaded; delivery legs
          take it from the collection row they draw from. */}
      {productOptions && phase === 'load' && (
        <CargoProductLines
          index={index}
          draft={draft}
          options={productOptions}
          multiProduct={multiProduct}
          disabled={disabled}
          onChange={onChange}
        />
      )}
      {family === 'container' && phase === 'load' && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <TextInput
            label="BIC code"
            name={`bic-${index}`}
            autoComplete="off"
            value={draft.identifier}
            disabled={disabled}
            maxLength={32}
            onChange={(e) => onChange({ identifier: e.target.value.toUpperCase() })}
          />
          <TextInput
            label="Seal"
            name={`seal-${index}`}
            autoComplete="off"
            value={draft.seal}
            disabled={disabled}
            maxLength={32}
            onChange={(e) => onChange({ seal: e.target.value })}
          />
          <NumberFieldInput
            label="Weight (kg)"
            value={draft.weightKg}
            disabled={disabled}
            onChange={(n) => onChange({ weightKg: n })}
          />
          <NumberFieldInput
            label="Volume (m³)"
            value={draft.volumeM3}
            disabled={disabled}
            onChange={(n) => onChange({ volumeM3: n })}
          />
        </div>
      )}
      {family === 'container' && phase === 'delivery' && (
        // Delivery-side: Seal flips to a status dropdown; Weight relabels
        // to "Offload" (measured on arrival, BE column still weightKg).
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <TextInput
            label="BIC code"
            name={`bic-${index}`}
            autoComplete="off"
            value={draft.identifier}
            disabled={disabled}
            maxLength={32}
            onChange={(e) => onChange({ identifier: e.target.value.toUpperCase() })}
          />
          <SelectInput
            label="Seal status"
            name={`seal-status-${index}`}
            value={draft.sealStatus}
            disabled={disabled}
            options={[
              { value: '', label: '—' },
              { value: 'Intact', label: 'Intact' },
              { value: 'Broken', label: 'Broken' },
              { value: 'Replaced', label: 'Replaced' },
            ]}
            onChange={(e) => onChange({ sealStatus: e.target.value as SealStatusValue | '' })}
          />
          <NumberFieldInput
            label="Offload (kg)"
            value={draft.weightKg}
            disabled={disabled}
            onChange={(n) => onChange({ weightKg: n })}
          />
          <NumberFieldInput
            label="Volume (m³)"
            value={draft.volumeM3}
            disabled={disabled}
            onChange={(n) => onChange({ volumeM3: n })}
          />
        </div>
      )}
      {family === 'tank' && phase === 'load' && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <TextInput
            label="Tank ID"
            name={`tank-id-${index}`}
            autoComplete="off"
            value={draft.identifier}
            disabled={disabled}
            maxLength={32}
            onChange={(e) => onChange({ identifier: e.target.value.toUpperCase() })}
          />
          <SelectInput
            label="Tank status"
            name={`tank-status-${index}`}
            value={draft.tankStatus}
            disabled={disabled}
            options={[
              { value: '', label: '—' },
              { value: 'EmptyClean', label: 'Empty (clean)' },
              { value: 'EmptyDirty', label: 'Empty (dirty)' },
              { value: 'Full', label: 'Full' },
            ]}
            onChange={(e) => onChange({ tankStatus: e.target.value as TankStatusValue | '' })}
          />
          <NumberFieldInput
            label="Capacity (L)"
            value={draft.capacity}
            disabled={disabled}
            onChange={(n) => onChange({ capacity: n })}
          />
          <NumberFieldInput
            label="Weight (kg)"
            value={draft.weightKg}
            disabled={disabled}
            onChange={(n) => onChange({ weightKg: n })}
          />
        </div>
      )}
      {family === 'tank' && phase === 'delivery' && (
        // Tank delivery: Weight relabels to "Discharge"; Capacity stays
        // on-screen as soft verification (operators occasionally correct
        // a tank's recorded capacity).
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <TextInput
            label="Tank ID"
            name={`tank-id-${index}`}
            autoComplete="off"
            value={draft.identifier}
            disabled={disabled}
            maxLength={32}
            onChange={(e) => onChange({ identifier: e.target.value.toUpperCase() })}
          />
          <SelectInput
            label="Tank status"
            name={`tank-status-${index}`}
            value={draft.tankStatus}
            disabled={disabled}
            options={[
              { value: '', label: '—' },
              { value: 'EmptyClean', label: 'Empty (clean)' },
              { value: 'EmptyDirty', label: 'Empty (dirty)' },
              { value: 'Full', label: 'Full' },
            ]}
            onChange={(e) => onChange({ tankStatus: e.target.value as TankStatusValue | '' })}
          />
          <NumberFieldInput
            label="Discharge (kg)"
            value={draft.weightKg}
            disabled={disabled}
            onChange={(n) => onChange({ weightKg: n })}
          />
          <NumberFieldInput
            label="Capacity (L)"
            value={draft.capacity}
            disabled={disabled}
            onChange={(n) => onChange({ capacity: n })}
          />
        </div>
      )}
      {family === 'pallet' && hasPalletTypes && (
        // Pallet cargo type: Pallet Type (stored by name in `description`)
        // + Quantity + Weight + a read-only Loading m total (variant's
        // per-pallet metres × quantity). Loading metres isn't persisted
        // — there's no BE column — it's a display-only derived value.
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_1fr_1fr]">
          <SelectInput
            label="Pallet type"
            name={`pallet-type-${index}`}
            value={draft.description}
            disabled={disabled}
            options={palletTypeOptions}
            onChange={(e) => onChange({ description: e.target.value })}
          />
          <NumberFieldInput
            label="Quantity (pallets)"
            value={draft.quantity}
            disabled={disabled}
            onChange={(n) => onChange({ quantity: n })}
          />
          <NumberFieldInput
            label="Weight (kg)"
            value={draft.weightKg}
            disabled={disabled}
            onChange={(n) => onChange({ weightKg: n })}
          />
          <TextInput
            label="Loading m"
            name={`loading-metres-${index}`}
            value={loadingMetresDisplay}
            readOnly
            tabIndex={-1}
          />
        </div>
      )}
      {family === 'pallet' && !hasPalletTypes && (
        // Other 'pallet'-family cargo types (FullLoad, PartLoad, AgriGrains, …)
        // have no type variants — free-text Description plus a *manual*
        // Loading m (no variant to derive it from).
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_1fr_1fr]">
          <TextInput
            label="Description"
            name={`description-${index}`}
            autoComplete="off"
            value={draft.description}
            disabled={disabled}
            maxLength={256}
            onChange={(e) => onChange({ description: e.target.value })}
          />
          <NumberFieldInput
            label="Quantity (pallets)"
            value={draft.quantity}
            disabled={disabled}
            onChange={(n) => onChange({ quantity: n })}
          />
          <NumberFieldInput
            label="Weight (kg)"
            value={draft.weightKg}
            disabled={disabled}
            onChange={(n) => onChange({ weightKg: n })}
          />
          <NumberFieldInput
            label="Loading m"
            value={draft.loadingMetresM}
            disabled={disabled}
            onChange={(n) => onChange({ loadingMetresM: n })}
          />
        </div>
      )}
    </div>
  );
}
